import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../components/layout/Sidebar';
import Header from '../components/layout/Header';
import { useAuth } from '../hooks';
import { API_BASE_URL } from '../constants';
import '../styles/Ledger.css';
import '../styles/Dashboard.css';

const Ledger = () => {
  const { currentUser, logout } = useAuth();
  const [showUserMenu, setShowUserMenu] = useState(false);
  const navigate = useNavigate();

  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedReport, setSelectedReport] = useState(null);
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    loadReports();
  }, []);

  const loadReports = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/ledger/reports`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken') || ''}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setReports(data.reports || []);
      }
    } catch (error) {
      console.error('Failed to load reports:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateReport = () => {
    // Navigate to new eKYB multi-step workflow
    navigate('/dashboard/ekyb-verification');
  };

  const handleViewReport = async (reportId) => {
    try {
      const response = await fetch(`${API_BASE_URL}/ledger/reports/${reportId}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken') || ''}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setSelectedReport(data);
        setShowModal(true);
      }
    } catch (error) {
      console.error('Failed to load report details:', error);
      alert('Failed to load report details');
    }
  };

  const handleDownloadReport = async (reportUrl) => {
    window.open(reportUrl, '_blank');
  };

  const getRiskBadgeClass = (rating) => {
    const ratingUpper = (rating || '').toUpperCase();
    if (ratingUpper === 'LOW') return 'risk-badge-low';
    if (ratingUpper === 'MEDIUM') return 'risk-badge-medium';
    if (ratingUpper === 'HIGH') return 'risk-badge-high';
    if (ratingUpper === 'REJECT') return 'risk-badge-reject';
    return 'risk-badge-medium';
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <>
      <Header
        currentUser={currentUser}
        showUserMenu={showUserMenu}
        setShowUserMenu={setShowUserMenu}
        onLogout={logout}
      />

      <div className="app">
        <Sidebar />

        <main className="main-content">
          <div className="content-area">
            {/* Hero Header */}
            <div className="ledger-hero">
              <div className="ledger-hero-content">
                <div className="ledger-hero-label">COMPLIANCE TERMINAL › LEDGER</div>
                <h1 className="ledger-hero-title">LEDGER Operator</h1>
                <p className="ledger-hero-description">
                  Generate formal, audit-ready compliance verification reports with immutable ledger records.
                  Each report is cryptographically hashed and timestamped for integrity verification.
                </p>
                <div className="ledger-hero-actions">
                  <button className="ledger-btn-primary" onClick={handleGenerateReport}>
                    <svg className="btn-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                    </svg>
                    Generate New Report
                  </button>
                </div>
              </div>
            </div>

            {/* Reports List */}
            <div className="ledger-content">
              {loading ? (
                <div className="ledger-loading">Loading ledger records...</div>
              ) : reports.length === 0 ? (
                <div className="ledger-empty">
                  <div className="empty-icon">
                    <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                  </div>
                  <h3 className="empty-title">No Reports Generated</h3>
                  <p className="empty-description">
                    Start by generating an eKYB verification report. Each report is cryptographically signed
                    and stored in the immutable ledger for audit purposes.
                  </p>
                  <button className="ledger-btn-primary" onClick={handleGenerateReport}>
                    Generate First Report
                  </button>
                </div>
              ) : (
                <>
                  <div className="ledger-header">
                    <h2 className="ledger-section-title">Generated Reports</h2>
                    <div className="ledger-stats">
                      <span className="stat-value">{reports.length}</span>
                      <span className="stat-label">Total Reports</span>
                    </div>
                  </div>

                  <div className="ledger-reports-list">
                    {reports.map((report) => (
                      <div key={report.id} className="ledger-report-card">
                        <div className="report-card-header">
                          <div className="report-card-meta">
                            <h3 className="report-entity-name">{report.entity_name}</h3>
                            <div className="report-info">
                              <span className="report-type">{report.report_type.replace(/_/g, ' ')}</span>
                              <span className="report-date">{formatDate(report.generated_at)}</span>
                            </div>
                          </div>
                          <div className={`risk-badge ${getRiskBadgeClass(report.risk_rating)}`}>
                            {report.risk_rating}
                          </div>
                        </div>

                        <div className="report-card-body">
                          {report.entity_reg_number && (
                            <div className="report-field">
                              <span className="field-label">Registration:</span>
                              <span className="field-value">{report.entity_reg_number}</span>
                            </div>
                          )}

                          {report.metadata && (
                            <>
                              {report.metadata.jurisdiction && (
                                <div className="report-field">
                                  <span className="field-label">Jurisdiction:</span>
                                  <span className="field-value">{report.metadata.jurisdiction}</span>
                                </div>
                              )}

                              {report.metadata.mica_status && (
                                <div className="report-field">
                                  <span className="field-label">MiCA Status:</span>
                                  <span className="field-value">{report.metadata.mica_status}</span>
                                </div>
                              )}

                              {report.metadata.aml_screening && (
                                <div className="report-field">
                                  <span className="field-label">AML Status:</span>
                                  <span className="field-value">
                                    {report.metadata.aml_screening.company}
                                    {report.metadata.aml_screening.pep_matches > 0 &&
                                      ` (${report.metadata.aml_screening.pep_matches} PEP matches)`
                                    }
                                  </span>
                                </div>
                              )}
                            </>
                          )}

                          <div className="report-field">
                            <span className="field-label">Report Hash:</span>
                            <span className="field-value report-hash">
                              {report.report_hash ? report.report_hash.substring(0, 16) + '...' : 'N/A'}
                            </span>
                          </div>
                        </div>

                        <div className="report-card-footer">
                          <button
                            className="report-action-btn"
                            onClick={() => handleViewReport(report.id)}
                          >
                            <svg className="btn-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                            </svg>
                            View Details
                          </button>

                          {report.report_url && (
                            <button
                              className="report-action-btn primary"
                              onClick={() => handleDownloadReport(report.report_url)}
                            >
                              <svg className="btn-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                              </svg>
                              Download Report
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>

            {/* Report Detail Modal */}
            {showModal && selectedReport && (
              <div className="ledger-modal-overlay" onClick={() => setShowModal(false)}>
                <div className="ledger-modal" onClick={(e) => e.stopPropagation()}>
                  <div className="modal-header">
                    <h2>Report Details</h2>
                    <button className="modal-close" onClick={() => setShowModal(false)}>
                      <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>

                  <div className="modal-body">
                    <div className="modal-section">
                      <h3>Report Information</h3>
                      <div className="modal-field">
                        <span>Report ID:</span>
                        <span>{selectedReport.id}</span>
                      </div>
                      <div className="modal-field">
                        <span>Entity:</span>
                        <span>{selectedReport.entity_name}</span>
                      </div>
                      <div className="modal-field">
                        <span>Generated:</span>
                        <span>{formatDate(selectedReport.generated_at)}</span>
                      </div>
                      <div className="modal-field">
                        <span>Generated By:</span>
                        <span>{selectedReport.generated_by}</span>
                      </div>
                    </div>

                    <div className="modal-section">
                      <h3>Ledger Record</h3>
                      <div className="modal-field">
                        <span>Report Hash (SHA-256):</span>
                        <code className="hash-display">{selectedReport.report_hash}</code>
                      </div>
                      <div className="modal-field">
                        <span>Immutable:</span>
                        <span className="badge-success">YES</span>
                      </div>
                      <div className="modal-field">
                        <span>Timestamp:</span>
                        <span>{selectedReport.generated_at}</span>
                      </div>
                    </div>

                    {selectedReport.metadata && (
                      <div className="modal-section">
                        <h3>Verification Details</h3>
                        <div className="modal-field">
                          <span>Frameworks Checked:</span>
                          <span>{selectedReport.metadata.frameworks_checked?.join(', ')}</span>
                        </div>
                        <div className="modal-field">
                          <span>Data Sources:</span>
                          <span>{selectedReport.metadata.verification_sources?.join(', ')}</span>
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="modal-footer">
                    {selectedReport.report_url && (
                      <button
                        className="ledger-btn-primary"
                        onClick={() => handleDownloadReport(selectedReport.report_url)}
                      >
                        Download Full Report
                      </button>
                    )}
                    <button
                      className="ledger-btn-secondary"
                      onClick={() => setShowModal(false)}
                    >
                      Close
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </main>
      </div>
    </>
  );
};

export default Ledger;
